// Same-origin fetch wrapper. The browser only ever talks to the UI host;
// the UI host carries auth (session cookie) and reverse-proxies into the
// per-user tailnet servers.
//
// Two flavors:
//   api(path)       — direct UI-host endpoints (/api/auth/..., /api/vps,
//                     /api/admin/users, /api/me/password)
//   apiVps(path)    — server-scoped calls; auto-prefixed with
//                     /api/vps/<active_vps_id>/proxy/api at call time. The
//                     /proxy/ segment isolates the reverse-proxy namespace
//                     from the static VPS endpoints (CRUD, token-rotate) so
//                     routes never overlap by method-confusion.
//
// `ApiError` carries the HTTP status so handlers can distinguish 401
// (session expired → bounce to login) from 4xx/5xx (show toast).

export class ApiError extends Error {
  status: number;
  body?: unknown;
  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

interface FetchOpts {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
}

// In-memory active VPS id. Set by AuthContext after /api/auth/me or after
// a successful enter; cleared on logout. Module-level so the api client
// doesn't need React context. Reads must be lazy — we only need it at the
// moment a request fires.
let activeVpsId: number | null = null;
export function setActiveVpsId(id: number | null) {
  activeVpsId = id;
}
export function getActiveVpsId(): number | null {
  return activeVpsId;
}

export async function api<T = unknown>(path: string, opts: FetchOpts = {}): Promise<T> {
  return rawFetch<T>(path, opts);
}

export async function apiVps<T = unknown>(path: string, opts: FetchOpts = {}): Promise<T> {
  if (activeVpsId === null) {
    throw new ApiError(409, "no active server — pick one first");
  }
  const sub = path.startsWith("/") ? path : `/${path}`;
  const prefixed = `/api/vps/${activeVpsId}/proxy/api${sub}`;
  return rawFetch<T>(prefixed, opts);
}

async function rawFetch<T>(path: string, opts: FetchOpts): Promise<T> {
  const method = opts.method ?? "GET";
  const init: RequestInit = {
    method,
    credentials: "same-origin",
    signal: opts.signal,
    headers: opts.body !== undefined ? { "content-type": "application/json" } : {},
  };
  if (opts.body !== undefined) {
    init.body = JSON.stringify(opts.body);
  }
  const res = await fetch(path, init);
  if (res.status === 204) {
    return undefined as T;
  }
  const ct = res.headers.get("content-type") ?? "";
  let body: unknown = undefined;
  if (ct.includes("application/json")) {
    body = await res.json().catch(() => undefined);
  } else {
    body = await res.text().catch(() => undefined);
  }
  if (!res.ok) {
    const msg =
      (body && typeof body === "object" && "error" in body && typeof body.error === "string"
        ? body.error
        : typeof body === "string" && body
          ? body
          : `${res.status} ${res.statusText}`);
    throw new ApiError(res.status, msg, body);
  }
  return body as T;
}
